import { DashboardComponentType, IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import {
  dashboardComponentOptions,
  dashboardGroupHeaderComponentOptions,
} from "components/component-mappings";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { OverlayPanel } from "primereact/overlaypanel";
import React, { useRef, useState } from "react";
import { T } from "services/lang/LanguageService";

interface ComponentTypeEditorProps {
  type?: DashboardComponentType;
  currentConfig: IDashboardComponent;
  setCurrentConfig: (currentConfig: IDashboardComponent) => void;
  isGroupHeaderComponent?: boolean;
}

const ComponentTypeEditor = (props: ComponentTypeEditorProps) => {
  const [tooltipImage, setTooltipImage] = useState<string | null>(null);
  const overlayRef = useRef<OverlayPanel>(null);
  const wasTypeClickedRef = useRef<boolean>(false);

  const handleUnselectTooltipImage = () => {
    overlayRef.current?.hide();
    setTooltipImage(null);
  };

  const handleTypeChange = (e: DropdownChangeEvent | { value: string }) => {
    wasTypeClickedRef.current = true;
    handleUnselectTooltipImage();

    const updatedComponent = {
      ...props.currentConfig,
      config: {},
      type: e.value,
    };

    props.setCurrentConfig(updatedComponent);
  };

  const handleMouseEnterTooltipImage = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    tooltipImg: string
  ) => {
    if (wasTypeClickedRef.current) return;
    setTooltipImage(tooltipImg);
    overlayRef.current?.show(e, e.target);
  };

  const handleMouseLeaveTooltipImage = () => {
    handleUnselectTooltipImage();
    wasTypeClickedRef.current = false;
  };

  const componentTypeItemTemplate = (option: { id: string; name: string; tooltipImage: string }) => {
    return (
      <div className="flex flex-row justify-content-between w-full">
        <div className="my-auto">{option.name}</div>
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-text p-button-plain p-0 h-1rem"
          onClick={() => handleTypeChange({ value: option.id })}
          onMouseEnter={e => handleMouseEnterTooltipImage(e, option.tooltipImage)}
          onMouseLeave={handleMouseLeaveTooltipImage}
        />
      </div>
    );
  };

  const componentOptions = props.isGroupHeaderComponent
    ? dashboardGroupHeaderComponentOptions
    : dashboardComponentOptions;

  return (
    <>
      <OverlayPanel ref={overlayRef} className="overlaypanel-content-background">
        {tooltipImage ? (
          <img src={tooltipImage} alt="Component preview" className="w-30rem border-round-lg" />
        ) : (
          <div>{T("message.no-preview-available")}</div>
        )}
      </OverlayPanel>
      <div className="p-col w-4">
        <label htmlFor="type">{T("label.component-type")}</label>
        <Dropdown
          value={props.type}
          options={componentOptions}
          filter
          onChange={handleTypeChange}
          optionLabel="name"
          optionValue="id"
          placeholder={T("action.select-a-component-type")}
          itemTemplate={componentTypeItemTemplate}
        />
      </div>
    </>
  );
};

export default ComponentTypeEditor;
